import { ReactNode } from 'react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import FeedbackIcon from '@mui/icons-material/Feedback'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import SettingsIcon from '@mui/icons-material/Settings'
import { createUrl } from '../../shared/UrlUtils/urlUtils'

export interface UserMenuProfile {
	firstName: string
	lastName: string
	username: string
	email: string
	guid: string
	isSubscribed: boolean
	subscriptionType: string
	restrictions: {
		hasCSEAccess: boolean
		hasTopicAccess: boolean
		hasDrSecDimAccess: boolean
	}
	image: string
}

export interface UserMenuContext {
	profile: UserMenuProfile
	authenticated: boolean | null
	onLogout: () => void
	/** Base URL of the SecDim ID frontend (VITE_SECDIM_ID_FRONTEND). */
	idFrontendUrl: string
	/** Fully-built account settings URL (AppRouteNames.settingsID). */
	settingsUrl: string
	/** Feedback redirect URL (VITE_SECDIM_LEARN_FEEDBACK_REDIRECT). */
	feedbackUrl: string
	/** Support site URL (VITE_SECDIM_SUPPORT_URL). */
	supportUrl: string
}

type VisibleFn = (ctx: UserMenuContext) => boolean

export interface ProfileSummaryItem {
	type: 'profileSummary'
	id: string
	displayName: string
	username: string
	plan: string
	image: string
	showAvatar: boolean
	profileHref: string
	visible?: VisibleFn
}

export interface LinkItem {
	type: 'link'
	id: string
	label: string
	href: string
	ariaLabel: string
	icon?: ReactNode
	visible?: VisibleFn
}

export interface ActionItem {
	type: 'action'
	id: string
	label: string
	onClick: () => void
	ariaLabel: string
	icon?: ReactNode
	visible?: VisibleFn
}

export interface LabelItem {
	type: 'label'
	id: string
	label: ReactNode
	visible?: VisibleFn
}

export type UserMenuItem =
	| ProfileSummaryItem
	| LinkItem
	| ActionItem
	| LabelItem

const isAuthenticated: VisibleFn = ctx => ctx.authenticated === true

/**
 * Substring the SecDim ID service puts in the filename of its placeholder
 * avatar (e.g. `.../media/DefaultImage.png`). ID exposes no "has custom avatar"
 * flag, so we infer it from the URL. This MUST match ID's default-avatar
 * filename — if ID renames it, update this constant. Ideally the API would
 * return a `has_custom_avatar` boolean and this heuristic would be removed.
 */
const ID_DEFAULT_AVATAR_MARKER = 'DefaultImage'

export const hasRealAvatar = (image: string): boolean =>
	image.length > 0 && !image.includes(ID_DEFAULT_AVATAR_MARKER)

/**
 * Local-dev only: rewrite the ID media host (8000 → 8001) so avatar URLs behave
 * the same as in Play during local development. Built (staging/production)
 * bundles serve ID media from real hosts, so the rewrite is skipped entirely.
 */
export const getProfileImageUrl = (image: string): string =>
	import.meta.env.DEV ? image.replace('localhost:8000', 'localhost:8001') : image

const getDisplayName = (profile: UserMenuProfile): string => {
	const fullName = `${profile.firstName} ${profile.lastName}`.trim()
	return fullName || profile.username || profile.email
}

const getUsername = (profile: UserMenuProfile): string =>
	profile.username || profile.email || profile.guid

const getPlan = (profile: UserMenuProfile): string => {
	const plan =
		profile.subscriptionType === '' ? 'Free' : profile.subscriptionType
	return `${plan} Plan`
}

/**
 * Build the user menu item array from context. Pure: it only reads the context
 * passed in and evaluates each item's `visible` predicate, returning the items
 * that should be shown. Adding a future menu entry should only touch this file.
 */
export function buildUserMenuItems(ctx: UserMenuContext): UserMenuItem[] {
	const { profile } = ctx
	const profileHref = createUrl(ctx.idFrontendUrl, {
		path: getUsername(profile),
	})

	const items: UserMenuItem[] = [
		{
			type: 'profileSummary',
			id: 'profile-summary',
			displayName: getDisplayName(profile),
			username: getUsername(profile),
			plan: getPlan(profile),
			image: getProfileImageUrl(profile.image),
			showAvatar: hasRealAvatar(profile.image),
			profileHref,
			visible: isAuthenticated,
		},
		{
			type: 'link',
			id: 'your-profile',
			label: 'Your Profile',
			href: profileHref,
			ariaLabel: 'View your profile',
			icon: <AccountCircleIcon />,
			visible: isAuthenticated,
		},
		{
			type: 'link',
			id: 'account-settings',
			label: 'Account Settings',
			href: ctx.settingsUrl,
			ariaLabel: 'Open account settings',
			icon: <SettingsIcon />,
			visible: isAuthenticated,
		},
		{
			type: 'link',
			id: 'feedback',
			label: 'Give Feedback',
			href: ctx.feedbackUrl,
			ariaLabel: 'Give feedback',
			icon: <FeedbackIcon />,
			visible: isAuthenticated,
		},
		{
			type: 'link',
			id: 'support',
			label: 'Support',
			href: ctx.supportUrl,
			ariaLabel: 'Get support',
			icon: <HelpOutlineIcon />,
			visible: isAuthenticated,
		},
		{
			type: 'action',
			id: 'logout',
			label: 'Logout',
			onClick: ctx.onLogout,
			ariaLabel: 'Log out',
			icon: <LogoutIcon />,
			visible: isAuthenticated,
		},
	]

	return items.filter(item => (item.visible ? item.visible(ctx) : true))
}
